import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowRight, X } from "lucide-react";
import { Button } from "./ui/button";
import { useMode } from "../context/ModeContext";

const TOUR_KEY = "codingdrills_tour_done";
const OLD_KEYS = ["codingdrills_tour_done_python", "codingdrills_tour_done_blocks", "codingdrills_tour_skipped"];
const PAD = 8;
const CARD_W = 320;
const CARD_H = 290; // estimate used for placement

const ls = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k) => { try { localStorage.setItem(k, "1"); } catch { /* private mode */ } },
  del: (k) => { try { localStorage.removeItem(k); } catch { /* private mode */ } },
};

// Re-arm the tour (used by "Show tips again")
export const resetTours = () => [TOUR_KEY, ...OLD_KEYS].forEach(ls.del);

// Replay the tour right now on the current page (the "How to play" button) - ignores the once-per-machine flag
const REPLAY_EVENT = "codingdrills:replay-tour";
export const replayTour = () => window.dispatchEvent(new Event(REPLAY_EVENT));

// One tour per machine: whoever finished/skipped any earlier per-mode tour is done too
const tourSeen = () => {
  if (OLD_KEYS.some((k) => ls.get(k) === "1")) { ls.set(TOUR_KEY); OLD_KEYS.forEach(ls.del); }
  return ls.get(TOUR_KEY) === "1";
};

const isVisible = (el) => !!el && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== "hidden";
const q = (sel) => document.querySelector(sel);
const one = (sel) => { const el = q(sel); return isVisible(el) ? { els: [el] } : null; };
const mobileMenuOpen = () => isVisible(q('[data-testid="mobile-menu-panel"]'));
const switchHint = (mobile) =>
  mobile
    ? " Prefer another style? Open the menu and pick Junior, Blocks or Python any time."
    : " Prefer another style? Pick Junior, Blocks or Python in the top bar any time - your points are tracked for each.";

const RUN_STEP = {
  id: "run",
  targets: () => {
    const els = ['[data-testid="test-code-btn"]', '[data-testid="nav-play-btn"]', '[data-testid="submit-code-btn"]'].map(q).filter(isVisible);
    return els.length ? { els } : null;
  },
  title: "Test, then Submit",
  body: () =>
    "\"Test with Custom Input\" runs your code on any input you type - no penalty. When it looks right, press Submit to check it against the secret tests and earn points.",
};

// One two-step tour per coding mode. Step 1 also points at the Python/Blocks switch.
const STEPS = {
  python: [
    {
      id: "editor",
      targets: () => one('[data-testid="code-editor"]'),
      pointAtSwitch: true,
      title: "Type your Python here",
      body: (mobile) => "Write your program in this editor. If the question has an example input, it's already filled in below so you can run your code straight away." + switchHint(mobile),
    },
    RUN_STEP,
  ],
  blocks: [
    {
      id: "canvas",
      targets: () => one('[data-testid="blocks-canvas"]'),
      pointAtSwitch: true,
      title: "Drag blocks to build your program",
      body: (mobile) => "The block menu is already open on the left. Drag blocks out of it and snap them under the \"when the program runs\" block - tap a category name to see its other blocks. The Python your blocks make appears live below the canvas." + switchHint(mobile),
    },
    RUN_STEP,
  ],
  junior: [
    {
      id: "grid",
      targets: () => one('[data-testid="nav-grid"]'),
      pointAtSwitch: true,
      title: "Get the drill to the treasure",
      body: (mobile) => "This is your drill's grid. The block menu beside it is already open: drag move, turn and repeat blocks out and snap them under \"when the drill starts\", then watch the drill follow them." + switchHint(mobile),
    },
    {
      id: "play",
      targets: () => {
        const els = ['[data-testid="nav-play-btn"]'].map(q).filter(isVisible);
        return els.length ? { els } : null;
      },
      title: "Press Play to run and grade",
      body: () => "Press Play to watch your program run as many times as you like. The moment the drill reaches the treasure, your solution is graded and you earn your points - no separate submit step.",
    },
  ],
};

const unionRect = (els) => {
  const rs = els.map((e) => e.getBoundingClientRect());
  const left = Math.min(...rs.map((r) => r.left)) - PAD;
  const top = Math.min(...rs.map((r) => r.top)) - PAD;
  const right = Math.max(...rs.map((r) => r.right)) + PAD;
  const bottom = Math.max(...rs.map((r) => r.bottom)) + PAD;
  return { left, top, width: right - left, height: bottom - top };
};

const switchTarget = () => {
  const desktop = q('[data-testid="mode-toggle"]');
  if (isVisible(desktop)) return { el: desktop, mobile: false };
  const burger = q('[data-testid="mobile-menu-btn"]');
  return isVisible(burger) ? { el: burger, mobile: true } : null;
};

// Spotlight tour shown once per machine, on the first visit, for whichever coding mode is active.
// Python: editor -> Test/Submit. Blocks: canvas -> Test/Submit. Never re-shown on later mode toggles.
export const FirstVisitTour = ({ ready = true }) => {
  const { mode, hasChosen } = useMode();
  const steps = STEPS[mode] || STEPS.python;
  const [step, setStep] = useState(-1); // -1 idle, 0/1 active, 2 finished
  const [rect, setRect] = useState(null);
  const [switchRect, setSwitchRect] = useState(null);
  const [mobile, setMobile] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const rafRef = useRef(null);
  const forceRef = useRef(false); // set by replayTour(): run even though this machine has seen the tour
  const [replay, setReplay] = useState(0);

  const finish = useCallback(() => { ls.set(TOUR_KEY); setStep(2); }, []);

  // "How to play": restart the tour for the current mode from step 1
  useEffect(() => {
    const onReplay = () => { forceRef.current = true; setStep(-1); setReplay((n) => n + 1); };
    window.addEventListener(REPLAY_EVENT, onReplay);
    return () => window.removeEventListener(REPLAY_EVENT, onReplay);
  }, []);

  // Mid-tour mode switch: restart with the steps of the new mode (still the same first visit)
  useEffect(() => { setStep((s) => (s === 2 ? 2 : -1)); }, [mode]);

  // Start once the mode has been chosen, this mode's targets exist and the phone menu is closed
  useEffect(() => {
    if (!ready || !hasChosen || step !== -1 || (tourSeen() && !forceRef.current)) return;
    let tries = 0;
    let cancelled = false;
    let timer;
    const tryStart = () => {
      if (cancelled) return;
      if (!mobileMenuOpen() && steps.every((s) => s.targets())) {
        forceRef.current = false;
        setStep(0);
        return;
      }
      if (++tries < 20) timer = setTimeout(tryStart, 500);
    };
    timer = setTimeout(tryStart, forceRef.current ? 150 : 900);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [ready, hasChosen, step, mode, steps, replay]);

  // Measure the current target (and keep it measured on scroll/resize/menu toggles)
  useLayoutEffect(() => {
    if (step < 0 || step > 1) return;
    const current = steps[step];
    const measure = () => {
      setMenuOpen(mobileMenuOpen());
      const t = current.targets();
      if (!t) { setRect(null); return; }
      setRect(unionRect(t.els));
      const sw = current.pointAtSwitch ? switchTarget() : null;
      setMobile(!!sw?.mobile);
      setSwitchRect(sw ? unionRect([sw.el]) : null);
    };
    const t = current.targets();
    if (t) {
      const r = unionRect(t.els);
      const offscreen = r.top < 72 || r.top + r.height > window.innerHeight - 24;
      if (offscreen) t.els[0].scrollIntoView({ block: "center", behavior: "smooth" });
    }
    const onMove = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => { rafRef.current = null; measure(); });
    };
    // Opening/closing the phone menu shifts the page: re-measure after the layout settles
    const onClick = () => { setTimeout(measure, 50); setTimeout(measure, 350); };
    const settle = setTimeout(measure, 450);
    measure();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    document.addEventListener("click", onClick, true);
    return () => {
      clearTimeout(settle);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
      document.removeEventListener("click", onClick, true);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [step, steps]);

  // Escape skips the tour
  useEffect(() => {
    if (step < 0 || step > 1) return;
    const onKey = (e) => { if (e.key === "Escape") finish(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step, finish]);

  // Hidden while the phone menu is open (the highlights would point at shifted content)
  if (step < 0 || step > 1 || !rect || menuOpen) return null;
  const current = steps[step];
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const below = rect.top + rect.height + 12;
  const placeBelow = below + CARD_H < vh;
  const placeAbove = !placeBelow && rect.top - CARD_H - 12 > 64;
  // Very tall targets (Blocks canvas): sit over the target's lower edge, no arrow
  const overlap = !placeBelow && !placeAbove;
  const cardTop = placeBelow ? below : overlap ? Math.max(72, vh - CARD_H - 12) : undefined;
  const cardBottom = placeAbove ? vh - rect.top + 12 : undefined;
  const cardLeft = Math.min(Math.max(12, rect.left + rect.width / 2 - CARD_W / 2), vw - CARD_W - 12);
  const arrowLeft = Math.min(Math.max(16, rect.left + rect.width / 2 - cardLeft - 8), CARD_W - 32);

  return (
    <div className="fixed inset-0 z-[80] pointer-events-none" data-testid="first-visit-tour" data-step={step + 1} data-mode={mode}>
      <AnimatePresence>
        <motion.div
          key={`spot-${mode}-${step}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25 }}
          className="absolute rounded-xl ring-2 ring-primary/90"
          style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height, boxShadow: "0 0 0 9999px rgba(10, 12, 20, 0.55)" }}
          data-testid="tour-spotlight"
        />
        {switchRect && (
          <motion.div
            key={`switch-${mode}-${step}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.25, delay: 0.2 }}
            className="absolute rounded-full ring-2 ring-primary/80 animate-pulse"
            style={{ left: switchRect.left, top: switchRect.top, width: switchRect.width, height: switchRect.height }}
            data-testid="tour-switch-pointer"
          />
        )}
      </AnimatePresence>
      <motion.div
        key={`card-${mode}-${step}`}
        initial={{ opacity: 0, y: placeBelow ? 8 : -8, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.25, delay: 0.1 }}
        className="absolute pointer-events-auto bg-card text-card-foreground border border-border rounded-2xl shadow-2xl p-4"
        style={{ left: cardLeft, top: cardTop, bottom: cardBottom, width: CARD_W }}
        role="dialog"
        aria-live="polite"
        aria-label={current.title}
        data-testid={`tour-step-${step + 1}`}
      >
        {!overlap && (
          <span
            className={`absolute w-4 h-4 bg-card border-border rotate-45 ${placeBelow ? "-top-2 border-l border-t" : "-bottom-2 border-r border-b"}`}
            style={{ left: arrowLeft }}
          />
        )}
        <div className="flex items-start justify-between gap-3 mb-1.5">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Quick tip {step + 1} of {steps.length}
          </p>
          <button type="button" onClick={finish} className="text-muted-foreground hover:text-foreground -mr-1 -mt-1 p-1 rounded-md" aria-label="Skip tutorial" data-testid="tour-skip-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
        <h3 className="font-heading font-bold text-base mb-1">{current.title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed mb-3">{current.body(mobile)}</p>
        <div className="flex items-center justify-between gap-2">
          <div className="flex gap-1.5" aria-hidden="true">
            {steps.map((s, i) => <span key={s.id} className={`h-1.5 rounded-full transition-all ${i === step ? "w-5 bg-primary" : "w-1.5 bg-muted-foreground/40"}`} />)}
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" className="rounded-full text-muted-foreground" onClick={finish} data-testid="tour-skip-all-btn">
              Skip tutorial
            </Button>
            {step < steps.length - 1 ? (
              <Button size="sm" className="rounded-full gap-1.5" onClick={() => setStep(step + 1)} data-testid="tour-next-btn">
                Next <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button size="sm" className="rounded-full gap-1.5" onClick={finish} data-testid="tour-done-btn">
                Got it
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};
